import { useEffect, useState } from 'react'

import { isSupported, onMessage } from '@firebase/messaging'
import { getMessaging, getToken } from 'firebase/messaging'
import Image from 'next/image'
import { toast } from 'react-toastify'

import SVGS from '@/assets/svgs'
import { firebase_app } from '@/config/firebase'
import { NotificationlApi } from '@/redux/api/notification'
import { INotification } from '@/redux/api/notification/notification.type'
import { replaceVariables } from '@/utils/replaceVariable'

import { useAppDispatch, useAppSelector } from './useRedux'

export const useFCMToken = () => {
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // const [isSupported, setIsSupported] = useState<boolean>(true)
  const [isBrowserSupported, setIsBrowserSupported] = useState<boolean>(true)
  const vapidKey = process.env.firebaseVapidKey
  const user = useAppSelector((state) => state.root.auth.user)
  const dispatch = useAppDispatch()

  const WithAvatar = (title = '', content = '', data = {} as any) => {
    const handleContent = () => {
      if (data?.senderNickname) {
        return content.replace(':senderName', data.senderNickname)
      }
      return content
    }
    const handleTitle = () => {
      if (data?.senderNickname) {
        return title.replace(':senderName', data.senderNickname)
      }
      return title
    }
    return (
      <div className="flex gap-3 pl-1">
        <Image src={SVGS.icLogo} alt={'icon'} />
        <div className="flex flex-col">
          <p className="text-white font-semibold">{handleTitle()}</p>
          <p className="text-sm text-zinc-400">{handleContent()}</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (typeof window === 'undefined') return // tránh SSR lỗi

    const initMessaging = async () => {
      const supported = await isSupported()
      if (!supported) {
        setIsBrowserSupported(false)
        setError('This browser does not support Firebase messaging.')
        return
      }

      try {
        const permission = Notification.permission
        if (permission !== 'granted') {
          const result = await Notification.requestPermission()
          if (result !== 'granted') {
            setError('Notification permission denied.')
            return
          }
        }

        const messaging = getMessaging(firebase_app)

        // Đăng ký SW và lấy token
        const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        const currentToken = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: swRegistration,
        })

        if (currentToken) {
          setToken(currentToken)
        } else {
          setError('No registration token available. Request permission to generate one.')
        }

        // Lắng nghe message foreground
        onMessage(messaging, (payload) => {
          console.log('FCM message received:', payload)

          const notificationContent = WithAvatar(
            replaceVariables(payload.notification?.title || '', payload?.data?.moreInfo || ''),
            replaceVariables(payload.notification?.body || '', payload?.data?.moreInfo || ''),
            payload.data,
          )

          toast(notificationContent, {
            position: 'top-right',
            autoClose: 5000,
            closeButton: true,
            pauseOnHover: true,
          })

          const dataNoti = {
            id: payload.data?.notificationId,
            title: payload.notification?.title || '',
            description: payload.notification?.body || '',
            isSeen: payload.data?.isSeen === '0' ? false : true,
            type: payload.data?.type,
            redirectUrl: payload.data?.redirectUrl,
            createdDate: payload.data?.createdDate || new Date(),
            avatar: payload.data?.avatar,
            receiver: {
              id: user?.id,
              username: user?.username,
              nickname: user?.nickname,
              avatarImage: user?.avatarImage,
            },
            sender: {
              id: payload.data?.receiverId,
              username: payload.data?.senderUsername,
              nickname: payload.data?.senderNickname,
              avatarImage: payload.data?.senderAvatarImage,
            },
            post: payload.data?.postId ? { id: payload.data?.postId } : null,
            transaction: null,
            comment: payload.data?.commentId ? { id: payload.data?.commentId } : null,
            chatChannel: null,
            event: payload.data?.eventId ? { id: payload.data?.eventId } : null,
            memberJoinStatus: payload.data?.memberJoinStatus ?? null,
            rqCoHostStatus: payload.data?.rqCoHostStatus
              ? {
                  formatted: payload.data?.rqCoHostStatus,
                  value: payload.data?.rqCoHostStatus,
                }
              : null,
            moreInfo: payload.data?.moreInfo,
          } as INotification

          dispatch(
            NotificationlApi.util.updateQueryData(
              'getInfiniteNotification',
              { page: 0, limit: 20, sort: 'DESC', type: undefined },
              (draft) => {
                if (draft.data) draft.data.unshift(dataNoti)
              },
            ),
          )

          dispatch(
            NotificationlApi.util.updateQueryData(
              'getNotification',
              { page: 0, limit: 20, sort: 'DESC', type: undefined },
              (draft) => {
                if (draft.data) draft.data.unshift(dataNoti)
              },
            ),
          )
        })
      } catch (err: any) {
        setError(err?.message || 'An unexpected error occurred with FCM.')
      }
    }

    initMessaging()
  }, [vapidKey, user?.id])

  return { token, error, isSupported: isBrowserSupported }
}
